// \src\pages\trabalhe-conosco\page.tsx

import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MathCaptcha from "@/components/base/MathCaptcha";
import { useAdmin } from "@/context/AdminContext";

const CAREERS_API_URL = "/api/trabalhe-conosco.php";

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

const benefits = [
  { icon: "ri-money-dollar-circle-line", title: "Salário Competitivo", text: "Remuneração acima da média do mercado" },
  { icon: "ri-heart-pulse-line", title: "Plano de Saúde", text: "Cobertura médica e odontológica" },
  { icon: "ri-book-open-line", title: "Treinamentos", text: "Capacitação contínua e crescimento" },
  { icon: "ri-restaurant-line", title: "Vale Refeição", text: "Vale refeição e alimentação" },
  { icon: "ri-bus-line", title: "Vale Transporte", text: "Transporte facilitado" },
  { icon: "ri-trophy-line", title: "Comissões", text: "Bônus por desempenho e metas" },
];

export default function TrabalheConosco() {
  const { data: adminData } = useAdmin();
  const activeVacancies = adminData.jobVacancies.filter((vacancy) => vacancy.active);
  const headerMedia = adminData.settings.careersHeaderMedia || "/images/propostas/01.jpg";

  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [captchaOk, setCaptchaOk] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleCaptchaVerify = useCallback((ok: boolean) => setCaptchaOk(ok), []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("mobile_alt") as string || "").trim();

    if (honeypot) {
      setFormStatus("success");
      formRef.current?.reset();
      setCaptchaOk(false);
      return;
    }

    if (!captchaOk) {
      setFormError("Por favor, resolva a verificação de segurança antes de enviar.");
      return;
    }

    setFormStatus("sending");
    setFormError("");

    const payload = new URLSearchParams();

    data.forEach((value, key) => {
      if (key !== "mobile_alt" && typeof value === "string") {
        payload.append(key, value);
      }
    });

    try {
      const res = await fetch(CAREERS_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });

      const responseText = await res.text();

      let parsed: {
        success?: boolean;
        message?: string;
        code?: string;
        meta?: {
          message?: string;
          detail?: string;
        };
      } = {};

      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* resposta não JSON */
      }

      const serverMsg =
        parsed.message ||
        parsed.meta?.message ||
        parsed.meta?.detail ||
        responseText;

      if (res.ok && (parsed.success === true || parsed.code === "OK")) {
        setFormStatus("success");
        formRef.current?.reset();
        setCaptchaOk(false);
      } else {
        setFormStatus("error");

        setFormError(
          serverMsg?.toLowerCase().includes("spam")
            ? "Envio identificado como spam. Tente novamente."
            : serverMsg || "Erro ao enviar. Tente novamente.",
        );
      }
    } catch {
      setFormStatus("error");
      setFormError("Falha de conexão. Verifique sua internet e tente novamente.");
    }
  };

  return (
    <>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        {/* Header */}
        <section className="relative h-[260px] md:h-[320px]">
          {isVideoMedia(headerMedia) ? (
            <video
              src={headerMedia}
              className="w-full h-full object-cover object-top"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Trabalhe Conosco"
            />
          ) : (
            <img
              src={headerMedia}
              alt="Trabalhe Conosco"
              className="w-full h-full object-cover object-top"
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <nav className="text-xs text-background-400 font-label mb-3">
              <Link to="/" className="hover:text-primary-400 cursor-pointer">
                Início
              </Link>

              <span className="mx-2">/</span>
              <span className="text-primary-400">Trabalhe Conosco</span>
            </nav>

            <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">
              Trabalhe Conosco
            </h1>

            <p className="text-background-300 text-sm max-w-lg">
              Faça parte do time M&amp;Z e construa sua carreira com a gente
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left */}
            <div className="flex-1">
              {/* Benefits */}
              <div className="mb-10">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-2">
                  Por que trabalhar na M&amp;Z?
                </h2>

                <p className="text-foreground-600 text-sm mb-6">
                  Oferecemos um ambiente de trabalho colaborativo, oportunidades de crescimento e benefícios competitivos.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {benefits.map((b) => (
                    <div
                      key={b.title}
                      className="bg-background-50 border border-background-200 rounded-lg p-4 text-center"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mx-auto mb-2">
                        <i className={`${b.icon} text-base`} />
                      </div>

                      <p className="font-label font-semibold text-foreground-900 text-xs mb-1">
                        {b.title}
                      </p>

                      <p className="text-foreground-500 text-xs">
                        {b.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vacancies teaser */}
              <div className="bg-primary-50 border border-primary-100 rounded-lg p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-briefcase-line text-primary-500" />
                  Vagas em Aberto
                </h3>

                <div className="space-y-3">
                  {activeVacancies.map((vacancy) => (
                    <div
                      key={vacancy.id}
                      className="flex items-start justify-between gap-3 bg-background-50 border border-background-200 rounded-md p-3"
                    >
                      <div className="min-w-0">
                        <p className="font-label font-semibold text-foreground-900 text-sm">
                          {vacancy.title}
                        </p>

                        <p className="text-foreground-500 text-xs">
                          {vacancy.type}
                        </p>

                        {vacancy.description && (
                          <p className="text-foreground-500 text-xs mt-1 leading-relaxed">
                            {vacancy.description}
                          </p>
                        )}
                      </div>

                      {vacancy.urgent && (
                        <span className="bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">
                          Urgente
                        </span>
                      )}
                    </div>
                  ))}

                  {activeVacancies.length === 0 && (
                    <div className="bg-background-50 border border-dashed border-background-300 rounded-md p-4 text-center">
                      <i className="ri-briefcase-line text-2xl text-foreground-300 block mb-2" />
                      <p className="text-foreground-600 text-sm font-label font-semibold">
                        Nenhuma vaga aberta no momento
                      </p>
                      <p className="text-foreground-400 text-xs mt-1">
                        Você ainda pode enviar seu currículo para o Banco de Talentos.
                      </p>
                    </div>
                  )}
                </div>

                <p className="text-foreground-500 text-xs mt-3">
                  {activeVacancies.length > 0
                    ? "Escolha uma das vagas abertas no formulário ao lado ou envie seu currículo para o Banco de Talentos."
                    : "Envie seu currículo para o Banco de Talentos e entraremos em contato quando surgir uma oportunidade compatível."}
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="w-full lg:w-[420px] shrink-0">
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">
                  Envie seu Currículo
                </h2>

                <p className="text-foreground-500 text-sm mb-6">
                  Preencha os dados abaixo e envie seu currículo diretamente para o RH.
                </p>

                {formStatus === "success" ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <i className="ri-checkbox-circle-line text-4xl text-green-500 mb-3 block" />

                    <h3 className="font-heading font-bold text-green-800 text-lg mb-2">
                      Currículo Enviado!
                    </h3>

                    <p className="text-green-700 text-sm">
                      Recebemos seu cadastro. Entraremos em contato caso seu perfil seja selecionado. Obrigado!
                    </p>

                    <button
                      onClick={() => {
                        setFormStatus("idle");
                        setFormError("");
                      }}
                      className="mt-4 bg-green-600 hover:bg-green-700 text-background-50 px-6 py-2 rounded-md text-sm font-label font-medium cursor-pointer transition-all whitespace-nowrap"
                    >
                      Enviar outro currículo
                    </button>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    id="trabalhe-conosco-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    {/* Honeypot */}
                    <div className="hp-field-alt" aria-hidden="true">
                      <input
                        type="text"
                        name="mobile_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Nome Completo{" "}
                        <span className="text-primary-500">*</span>
                      </label>

                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Seu nome completo"
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        E-mail{" "}
                        <span className="text-primary-500">*</span>
                      </label>

                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="seu@email.com"
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Telefone / WhatsApp{" "}
                        <span className="text-primary-500">*</span>
                      </label>

                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(11) 99999-9999"
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Vaga de Interesse{" "}
                        <span className="text-primary-500">*</span>
                      </label>

                      <select
                        name="position"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Selecione uma vaga</option>
                        {activeVacancies.map((vacancy) => (
                          <option key={vacancy.id} value={vacancy.title}>
                            {vacancy.title}
                          </option>
                        ))}
                        <option value="Banco de Talentos">
                          Banco de Talentos
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Experiência Profissional{" "}
                        <span className="text-primary-500">*</span>
                      </label>

                      <select
                        name="experience"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Selecione...</option>
                        <option value="Sem experiência">
                          Sem experiência
                        </option>
                        <option value="Menos de 1 ano">
                          Menos de 1 ano
                        </option>
                        <option value="1 a 3 anos">
                          1 a 3 anos
                        </option>
                        <option value="3 a 5 anos">
                          3 a 5 anos
                        </option>
                        <option value="Mais de 5 anos">
                          Mais de 5 anos
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Resumo Profissional
                      </label>

                      <textarea
                        name="resume_summary"
                        rows={4}
                        maxLength={500}
                        placeholder="Conte um pouco sobre sua experiência, habilidades e objetivos profissionais..."
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      />

                      <p className="text-xs text-foreground-400 font-label mt-1">
                        Máximo de 500 caracteres
                      </p>
                    </div>

                    <MathCaptcha onVerify={handleCaptchaVerify} />

                    {formError && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-red-700 text-sm font-label flex items-center gap-2">
                          <i className="ri-error-warning-line" />
                          {formError}
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === "sending" || !captchaOk}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === "sending" ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin" />
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-send-plane-line" />
                          Enviar Candidatura
                        </span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}